// Risk Game History Service
// Fetches and parses all game events to reconstruct historical game states


export class RiskHistoryService {
  constructor(contract, gameId) {
    this.contract = contract;
    this.gameId = gameId;
    this.eventCache = new Map();
  }

  // Helper function to safely convert BigNumber to number
  safeToNumber(value) {
    if (value && typeof value.toNumber === 'function') {
      return value.toNumber();
    }
    return Number(value);
  }

  /**
   * Fetches all game events chronologically and builds complete history
   */
  async fetchGameHistory() {
    try {
      console.log(`Fetching history for game ${this.gameId}`);
      
      // Get all relevant events for this game
      const events = await this.fetchAllGameEvents();
      
      // Sort events by block number and transaction index
      const sortedEvents = this.sortEventsByTimestamp(events);
      
      // Parse events into structured data
      const parsedEvents = this.parseGameEvents(sortedEvents);
      
      // Build game state history
      const stateHistory = await this.buildStateHistory(parsedEvents);
      
      console.log(`History built: ${stateHistory.length} turns`);
      return stateHistory;
      
    } catch (error) {
      console.error('Error fetching game history:', error);
      throw error;
    }
  }

  /**
   * Fetches all contract events related to this game
   */
  async fetchAllGameEvents() {
    try {
      console.log(`Fetching events for game ${this.gameId}`);
      
      // Fetch all relevant events for this game using Ethers.js
      const events = [];
      
      // GameInitiated events
      const gameInitiatedFilter = this.contract.filters.GameInitiated(this.gameId);
      // Query in 10-block chunks to respect Alchemy free tier limits
      const currentBlock = await this.contract.provider.getBlockNumber();
      const fromBlock = Math.max(0, currentBlock - 1000); // Search last 1000 blocks max
      const gameInitiatedEvents = [];
      for (let start = fromBlock; start <= currentBlock; start += 10) {
        const end = Math.min(start + 9, currentBlock);
        try {
          const batch = await this.contract.queryFilter(gameInitiatedFilter, start, end);
          gameInitiatedEvents.push(...batch);
        } catch (error) {
          console.warn(`Failed to query GameInitiated events for blocks ${start}-${end}:`, error.message);
        }
      }
      events.push(...gameInitiatedEvents.map(e => ({ ...e, event: 'GameInitiated' })));
      
      // Helper function to query events in 10-block chunks
      const queryEventsInChunks = async (filter, eventName) => {
        const events = [];
        for (let start = fromBlock; start <= currentBlock; start += 10) {
          const end = Math.min(start + 9, currentBlock);
          try {
            const batch = await this.contract.queryFilter(filter, start, end);
            events.push(...batch);
          } catch (error) {
            console.warn(`Failed to query ${eventName} events for blocks ${start}-${end}:`, error.message);
          }
        }
        return events;
      };

      // TurnSubmitted events
      const turnSubmittedFilter = this.contract.filters.TurnSubmitted(this.gameId);
      const turnSubmittedEvents = await queryEventsInChunks(turnSubmittedFilter, 'TurnSubmitted');
      events.push(...turnSubmittedEvents.map(e => ({ ...e, event: 'TurnSubmitted' })));

      // Combat events
      const combatFilter = this.contract.filters.Combat(this.gameId);
      const combatEvents = await queryEventsInChunks(combatFilter, 'Combat');
      events.push(...combatEvents.map(e => ({ ...e, event: 'Combat' })));

      // TerritoryConquered events
      const conqueredFilter = this.contract.filters.TerritoryConquered(this.gameId);
      const conqueredEvents = await queryEventsInChunks(conqueredFilter, 'TerritoryConquered');
      events.push(...conqueredEvents.map(e => ({ ...e, event: 'TerritoryConquered' })));

      // GameEnded events
      const gameEndedFilter = this.contract.filters.GameEnded(this.gameId);
      const gameEndedEvents = await queryEventsInChunks(gameEndedFilter, 'GameEnded');
      events.push(...gameEndedEvents.map(e => ({ ...e, event: 'GameEnded' })));

      // GameKilled events
      const gameKilledFilter = this.contract.filters.GameKilled(this.gameId);
      const gameKilledEvents = await queryEventsInChunks(gameKilledFilter, 'GameKilled');
      events.push(...gameKilledEvents.map(e => ({ ...e, event: 'GameKilled' })));
      
      console.log(`Found ${events.length} events for game ${this.gameId}`);
      return events;
      
    } catch (error) {
      console.error('Error fetching game events:', error);
      return [];
    }
  }

  /**
   * Sorts events by block number, then transaction index, then log index
   */
  sortEventsByTimestamp(events) {
    return events.sort((a, b) => {
      // Primary sort: block number
      if (a.blockNumber !== b.blockNumber) {
        return a.blockNumber - b.blockNumber;
      }
      
      // Secondary sort: transaction index
      if (a.transactionIndex !== b.transactionIndex) {
        return a.transactionIndex - b.transactionIndex;
      }
      
      // Tertiary sort: log index
      return a.logIndex - b.logIndex;
    });
  }

  /**
   * Parses raw events into structured data objects
   */
  parseGameEvents(events) {
    return events.map(event => {
      const baseEvent = {
        type: event.event,
        blockNumber: event.blockNumber,
        transactionHash: event.transactionHash,
        transactionIndex: event.transactionIndex,
        logIndex: event.logIndex,
        timestamp: null, // Will be filled by block timestamp
        args: {}
      };

      // Parse event-specific data (Ethers.js format)
      switch (event.event) {
        case 'GameInitiated':
          baseEvent.args = {
            gameId: this.safeToNumber(event.args.gameId),
            player1: event.args.player1,
            player2: event.args.player2
          };
          break;

        case 'TurnSubmitted':
          baseEvent.args = {
            gameId: this.safeToNumber(event.args.gameId),
            player: event.args.player,
            numMoves: this.safeToNumber(event.args.numMoves)
          };
          break;

        case 'Combat':
          baseEvent.args = {
            gameId: this.safeToNumber(event.args.gameId),
            territory: this.safeToNumber(event.args.territory),
            attackers: this.safeToNumber(event.args.attackers),
            defenders: this.safeToNumber(event.args.defenders),
            attackerLosses: this.safeToNumber(event.args.attackerLosses),
            defenderLosses: this.safeToNumber(event.args.defenderLosses)
          };
          break;

        case 'TerritoryConquered':
          baseEvent.args = {
            gameId: this.safeToNumber(event.args.gameId),
            territory: this.safeToNumber(event.args.territory),
            newOwner: event.args.newOwner
          };
          break;

        case 'GameEnded':
          baseEvent.args = {
            gameId: this.safeToNumber(event.args.gameId),
            winner: event.args.winner
          };
          break;

        case 'GameKilled':
          baseEvent.args = {
            gameId: this.safeToNumber(event.args.gameId),
            refund_amount_player1: event.args.refund_amount_player1,
            refund_amount_player2: event.args.refund_amount_player2,
            remainder: event.args.remainder
          };
          break;

        default:
          console.warn(`Unknown event type: ${event.event}`);
      }

      return baseEvent;
    });
  }

  /**
   * Builds complete game state history from parsed events
   */
  async buildStateHistory(events) {
    const stateHistory = [];
    let currentState = null;
    let currentTurnEvents = [];
    let turnStateBefore = null;

    // Get initial game state after GameInitiated
    const gameInitEvent = events.find(e => e.type === 'GameInitiated');
    if (!gameInitEvent) {
      throw new Error('GameInitiated event not found');
    }

    // Get initial territory state from contract
    currentState = await this.getInitialGameState(gameInitEvent.args.player1, gameInitEvent.args.player2);

    // Add timestamps to events
    await this.addTimestampsToEvents(events);

    // Group events by turns (each turn starts with TurnSubmitted)
    const turnGroups = [];
    let currentTurnGroup = [];

    for (const event of events) {
      if (event.type === 'GameInitiated') {
        continue;
      }

      if (event.type === 'TurnSubmitted') {
        // Save previous turn group if exists
        if (currentTurnGroup.length > 0) {
          turnGroups.push([...currentTurnGroup]);
        }
        // Start new turn group
        currentTurnGroup = [event];
      } else {
        // Add to current turn group
        currentTurnGroup.push(event);
      }
    }

    // Don't forget the last turn group
    if (currentTurnGroup.length > 0) {
      turnGroups.push(currentTurnGroup);
    }

    // Process each turn group to build proper state snapshots
    for (let turnIndex = 0; turnIndex < turnGroups.length; turnIndex++) {
      const turnEvents = turnGroups[turnIndex];
      const turnSubmittedEvent = turnEvents.find(e => e.type === 'TurnSubmitted');

      if (!turnSubmittedEvent) continue;

      // Capture state BEFORE processing this turn's events
      const stateBefore = JSON.parse(JSON.stringify(currentState));

      // Apply all events in this turn to update the state
      for (const event of turnEvents) {
        if (event.type === 'Combat' || event.type === 'TerritoryConquered') {
          this.applyEventToState(currentState, event);
        }
      }

      // Capture state AFTER processing this turn's events
      const stateAfter = JSON.parse(JSON.stringify(currentState));

      // Determine if this is a game-ending turn
      const gameEndEvent = turnEvents.find(e => e.type === 'GameEnded' || e.type === 'GameKilled');

      // Create turn history entry
      const turnEntry = {
        turnNumber: turnIndex + 1,
        player: this.getPlayerFromAddress(currentState, turnSubmittedEvent.args.player),
        events: [...turnEvents],
        stateBefore,
        stateAfter,
        timestamp: turnSubmittedEvent.timestamp,
        transactionHash: turnSubmittedEvent.transactionHash
      };

      // Add game end info if applicable
      if (gameEndEvent) {
        turnEntry.gameEnded = true;
        turnEntry.winner = gameEndEvent.args.winner;
      }

      stateHistory.push(turnEntry);

      // Break if game ended
      if (gameEndEvent) break;
    }

    return stateHistory;
  }

  /**
   * Gets initial game state after random territory assignment
   */
  async getInitialGameState(player1, player2) {
    try {
      // Use the current game state method which gets properly assigned territories
      const currentState = await this.getCurrentGameState();

      if (currentState && currentState.territoryOwners && currentState.territoryUnits) {
        // Check if territories are properly assigned (not all zeros)
        const assignedTerritories = currentState.territoryOwners.filter(owner => owner !== 0).length;

        if (assignedTerritories > 0) {
          console.log(`History service: Using current state as initial (${assignedTerritories} territories assigned)`);
          return {
            player1,
            player2,
            territoryOwners: currentState.territoryOwners,
            territoryUnits: currentState.territoryUnits,
            currentPlayer: currentState.currentPlayer,
            gameActive: currentState.gameActive
          };
        }
      }

      console.log('History service: Falling back to empty initial state');
      // Fallback to empty state
      return {
        player1,
        player2,
        territoryOwners: new Array(50).fill(0),
        territoryUnits: new Array(50).fill(0),
        currentPlayer: 1,
        gameActive: true
      };
    } catch (error) {
      console.error('Error getting initial game state:', error);
      // Return default state if contract call fails
      return {
        player1,
        player2,
        territoryOwners: new Array(50).fill(0),
        territoryUnits: new Array(50).fill(0),
        currentPlayer: 1,
        gameActive: true
      };
    }
  }

  /**
   * Applies combat/conquest events to current state
   */
  applyEventToState(state, event) {
    if (event.type === 'Combat') {
      // Combat affects unit counts
      const territory = event.args.territory;
      const defenders = event.args.defenders;
      const defenderLosses = event.args.defenderLosses;
      
      // Update defender units
      state.territoryUnits[territory] = defenders - defenderLosses;
      
    } else if (event.type === 'TerritoryConquered') {
      // Territory changes ownership
      const territory = event.args.territory;
      const newOwnerAddress = event.args.newOwner;
      const newOwnerNumber = newOwnerAddress === state.player1 ? 1 : 2;
      
      state.territoryOwners[territory] = newOwnerNumber;
    }
  }

  /**
   * Adds block timestamps to events
   */
  async addTimestampsToEvents(events) {
    try {
      const blockNumbers = [...new Set(events.map(e => e.blockNumber))];
      const blockPromises = blockNumbers.map(blockNum => 
        this.contract.provider.getBlock(blockNum)
      );
      
      const blocks = await Promise.all(blockPromises);
      const blockTimestamps = new Map();
      
      blocks.forEach(block => {
        blockTimestamps.set(block.number, block.timestamp);
      });
      
      events.forEach(event => {
        event.timestamp = blockTimestamps.get(event.blockNumber);
      });
    } catch (error) {
      console.error('Error fetching block timestamps:', error);
      // Set default timestamps if fetching fails
      events.forEach(event => {
        event.timestamp = Date.now() / 1000; // Current time as fallback
      });
    }
  }

  /**
   * Helper to determine player number from address
   */
  getPlayerFromAddress(state, address) {
    if (address === state.player1) return 1;
    if (address === state.player2) return 2;
    return null;
  }

  /**
   * Gets current game state for comparison
   */
  async getCurrentGameState() {
    try {
      // Get full game instance data from public mapping (contains all territory info)
      const gameInstance = await this.contract.gameInstances(this.gameId);
      
      // Handle case where game exists but territories aren't initialized yet
      let territoryOwners, territoryUnits;
      
      if (!gameInstance.territoryOwners || !gameInstance.territoryUnits) {
        console.log('getCurrentGameState: Game exists but territories not initialized yet.');
        territoryOwners = new Array(50).fill(0);
        territoryUnits = new Array(50).fill(0);
      } else {
        territoryOwners = gameInstance.territoryOwners.map(owner => this.safeToNumber(owner));
        territoryUnits = gameInstance.territoryUnits.map(units => this.safeToNumber(units));
      }
      
      return {
        territoryOwners,
        territoryUnits,
        currentPlayer: this.safeToNumber(gameInstance.currentPlayer),
        gameActive: gameInstance.gameActive,
        lastMoveTime: this.safeToNumber(gameInstance.lastMoveTime)
      };
    } catch (error) {
      console.error('Error getting current game state:', error);
      return null;
    }
  }
}

export default RiskHistoryService;